// Login.js - Customer and provider login
// Two column layout matching the signup page
// Checks user role after login and redirects accordingly:
//   outOfArea (any role)              → /out-of-area
//   provider, unverified              → /verify-email
//   provider, onboarding incomplete   → /provider/onboarding
//   provider, terms not ack'd         → /terms-acknowledge-provider
//   provider, agreement not signed    → /provider/agreement
//   provider, handbook not ack'd      → /provider/handbook
//   provider, fully set up            → /provider/dashboard
//   customer, unverified              → /verify-email
//   customer, in-area                 → /dashboard

import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    try {
      setLoading(true);
      setError('');

      // Authenticate with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Fetch Firestore profile to determine role and service area status
      const userSnap = await getDoc(doc(db, 'users', user.uid));

      if (userSnap.exists()) {
        const userData = userSnap.data();

        if (userData.outOfArea) {
          // User (any role) is outside our service area.
          // OutOfArea.js checks expansionRequests and shows thank-you if
          // they have already submitted a request, form otherwise.
          navigate('/out-of-area');

        } else if (userData.role === 'provider') {
          // Walk provider through every required step in order
          if (!user.emailVerified) {
            navigate('/verify-email');
          } else if (!userData.onboardingComplete) {
            navigate('/provider/onboarding');
          } else if (!userData.termsAcknowledged) {
            navigate('/terms-acknowledge-provider');
          } else if (!userData.contractorAgreementSigned) {
            navigate('/provider/agreement');
          } else if (!userData.handbookAcknowledged) {
            navigate('/provider/handbook');
          } else {
            navigate('/provider/dashboard');
          }

        } else if (!user.emailVerified) {
          // Customer hasn't verified their email yet — send to verification gate
          navigate('/verify-email');

        } else {
          // In-area verified customer — normal dashboard
          navigate('/dashboard');
        }

      } else {
        // No Firestore doc found — fall back to dashboard and let it handle it
        navigate('/dashboard');
      }

    } catch (err) {
      // Generic message for all auth failures — avoids leaking whether
      // an email address exists in the system (user enumeration)
      if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Invalid email or password.');
      }
      setLoading(false);
    }
  };

  // Sends a password reset email via Firebase Auth
  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) { setResetError('Enter your email address.'); return; }
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
    } catch {
      // Silently ignore — always show success so email existence isn't leaked
    }
    setResetSent(true);
    setResetError('');
  };

  return (
    <div className="auth-split-page">

      {/* ========== LEFT PANEL ========== */}
      <div className="auth-split-left">

        <a href="/" className="auth-split-logo">Evermark Memorial</a>

        <div className="auth-split-message">
          <h2>Welcome back.</h2>
          <p>
            Your loved ones are being remembered.
            Log in to view your photo reports and manage your visits.
          </p>
        </div>

        <div className="auth-trust-points">
          <div className="auth-trust-item">
            <span className="auth-trust-icon">🛡️</span>
            <span>Background checked caretakers</span>
          </div>
          <div className="auth-trust-item">
            <span className="auth-trust-icon">📸</span>
            <span>Before & after photo proof</span>
          </div>
          <div className="auth-trust-item">
            <span className="auth-trust-icon">💚</span>
            <span>Caring, respectful service</span>
          </div>
          <div className="auth-trust-item">
            <span className="auth-trust-icon">🔒</span>
            <span>Secure payments</span>
          </div>
        </div>

        <div className="auth-split-quote">
          <p>"The peace of knowing someone is there,<br />even when you can't be."</p>
        </div>

      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div className="auth-split-right">
        <div className="auth-card">
          <h2>Welcome back</h2>
          <p className="auth-subtitle">Log in to manage your visits</p>

          {error && <div className="auth-error">{error}</div>}

          <div className="auth-field">
            <label>Email Address</label>
            <input
              type="email"
              placeholder="john@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
            />
          </div>

          <div className="auth-field">
            <label>Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={128}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(prev => !prev)}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Forgot password toggle link */}
          <p className="forgot-password-link">
            <button type="button" className="btn-forgot" onClick={() => setShowForgotPassword(!showForgotPassword)}>
              Forgot password?
            </button>
          </p>

          {/* Forgot password inline form — shown when toggle clicked */}
          {showForgotPassword && (
            <div className="forgot-password-form">
              {resetSent ? (
                <p className="reset-success">Check your email — a reset link has been sent.</p>
              ) : (
                <>
                  <div className="auth-field">
                    <label>Your email address</label>
                    <input
                      type="email"
                      placeholder="you@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      maxLength={254}
                    />
                  </div>
                  {resetError && <p className="auth-error" style={{ marginBottom: '8px' }}>{resetError}</p>}
                  <button className="btn-primary btn-full" onClick={handlePasswordReset}>
                    Send Reset Email
                  </button>
                </>
              )}
            </div>
          )}

          <button
            className="btn-primary btn-full"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>

          <p className="auth-switch">
            Don't have an account? <a href="/signup">Sign up</a>
          </p>

          <p className="auth-switch" style={{ marginTop: '8px' }}>
            Are you a caretaker? <a href="/provider/signup">Sign up here</a>
          </p>
        </div>
      </div>

    </div>
  );
}

export default Login;
